import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  processReportDetails,
  ProcessedReportDetails,
} from '../../../functions/process-report-details';
import { ScheduledReport } from '../../../models/api/reports/all-reports-response';
import { BaseComponent } from '../../base/base.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { IncludeLabelComponent } from '../../core/labels/include-label.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';
import { OutputDetails } from '../../../models/api/reports/output-details';
import { CasePipe } from '../../../pipes/case.pipe';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { ReportsService } from '../../../services/reports/reports.service';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-scheduled-request-view',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CommonModule,
    HelpIconComponent,
    IncludeLabelComponent,
    CasePipe,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loading ? 'Loading' : 'Loaded'"
      loadingStyle="Box"
      [loadingHeight]="192"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-8 tw-w-full">
          <div class="sm:tw-basis-[40%]">
            <app-detail-panel [dataSource]="scheduledReportsData">
              <app-detail-item label="Name" name="reportName" />
              <app-detail-item label="Base report" name="outputName">
                <ng-template #value>
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <span>{{ scheduledReportsData.outputName }}</span>
                    <app-help-icon
                      [helpText]="reportDescription"
                      [ariaLabel]="
                        (scheduledReportsData.outputName ?? 'Base report') +
                        ' information'
                      "
                    />
                  </div>
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Client">
                <ng-template #value>
                  @if (
                    scheduledReportsData.contract &&
                    scheduledReportsData.clientName
                  ) {
                    {{ scheduledReportsData.contract }} -
                    {{ scheduledReportsData.clientName | case: 'titlecase' }}
                  }
                </ng-template>
              </app-detail-item>
              <app-detail-item
                label="Last run"
                name="lastRunDate"
                format="dt"
              />
              <app-detail-item
                label="Next run"
                name="nextRunDate"
                format="dt"
              />
              @if (asOfDate) {
                <app-detail-item label="As of Date">
                  <ng-template #value>{{ asOfDate }}</ng-template>
                </app-detail-item>
              }
              <!-- Commented out: Display all otherCriteria if requirements change
              @for (item of processed.otherCriteria; track $index) {
              <app-detail-item [label]="item.label">
                <ng-template #value>{{ item.value }}</ng-template>
              </app-detail-item>
              }
              -->
            </app-detail-panel>
          </div>

          <div class="sm:tw-basis-[30%]">
            <app-detail-panel [dataSource]="scheduledReportsData">
              <app-detail-item label="Frequency" name="requestFrequency" />
              <app-detail-item label="Format" name="format">
                <ng-template #value>
                  {{ processed.format }}
                </ng-template>
              </app-detail-item>
              @if (processed.format !== 'PDF') {
                <app-detail-item label="Auto-fit column widths" format="b">
                  <ng-template #value>
                    {{ processed.autoFitColumnWidths }}
                  </ng-template>
                </app-detail-item>
              }
              <!-- @if (processed.pdfLogoSelection) {
              <app-detail-item label="PDF logo selection">
                <ng-template #value>
                  {{ processed.pdfLogoSelection }}
                </ng-template>
              </app-detail-item>
              } -->
            </app-detail-panel>
          </div>

          <div class="sm:tw-basis-[30%]">
            <app-detail-panel [dataSource]="processed.includeItems">
              <app-detail-item label="Include">
                <ng-template #value>
                  <app-include-label
                    [include]="processed.includeItems"
                    [exclude]="processed.excludeItems"
                  />
                </ng-template>
              </app-detail-item>
            </app-detail-panel>
          </div>
        </div> </ng-template
    ></app-loading-panel>
  `,
  styles: ``,
})
export class ScheduledRequestViewComponent
  extends BaseComponent
  implements OnInit
{
  private reportsService = inject(ReportsService);

  @Input()
  scheduledReportsData: ScheduledReport;

  @Input()
  scheduledReportDetails: OutputDetails[] = [];

  processed: ProcessedReportDetails;

  reportDescription: string;

  loading = false;
  error: any = null;

  get asOfDate(): string | undefined {
    return this.processed?.otherCriteria?.find(
      (item) => item.label === 'As of Date',
    )?.value;
  }

  @HandleLoadingState()
  async ngOnInit() {
    this.scheduledReportsData.requestFrequency = this.getFrequencyLabel(
      this.scheduledReportsData.requestFrequency,
    );

    this.processed = processReportDetails(this.scheduledReportDetails);
    this.reportDescription = await this.reportsService.getReportDescription(
      this.scheduledReportsData.reportMasterId,
    );
  }

  getFrequencyLabel(frequency: string): string {
    const frequencyMap: Record<string, string> = {
      N: 'Once',
      D: 'Daily',
      W: 'Weekly',
      M: 'Monthly',
      Q: 'Quarterly',
      S: 'Semi-Annual',
      A: 'Annual',
    };

    return frequencyMap[frequency] || frequency;
  }
}
